import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ModuleAccessService } from './module-access.service';
import { OperationAccessService } from './operation-access.service';
import { OperationId, can } from './permission-keys';
import { requiresPermission } from '../../feature/permissions/models/Module';

const ROUTE_MODULE_OVERRIDES: Record<string, string> = {
  'verify-guest': 'guests',
  'equifax-lookup': 'guests',
  'equifax-lookup/detail/:id': 'guests'
};

export const resolveModuleIdForPath = (path: string): string | undefined => {
  if (!path) {
    return undefined;
  }

  if (ROUTE_MODULE_OVERRIDES[path]) {
    return ROUTE_MODULE_OVERRIDES[path];
  }

  return path.split('/')[0];
};

export const moduleAccessGuard: CanActivateFn = async (route) => {
  const moduleAccessService = inject(ModuleAccessService);
  const operationAccessService = inject(OperationAccessService);
  const router = inject(Router);

  const path = route.routeConfig?.path ?? '';
  const moduleId = resolveModuleIdForPath(path);

  if (!moduleId || moduleId === 'dashboard') {
    return true;
  }

  if (moduleId === 'permissions') {
    const isSuperAdmin = await moduleAccessService.isSuperAdmin();
    return isSuperAdmin || router.parseUrl('/error');
  }

  // Only permission-gated modules are checked; the rest are open to every signed-in user.
  if (!requiresPermission(moduleId)) {
    return true;
  }

  const required = requiredOperations(path).map(operationId => can(moduleId, operationId));

  const allowed = await moduleAccessService.hasAccess(moduleId)
    && await operationAccessService.hasPermission(required, 'all');
  return allowed || router.parseUrl('/error');
};

// Every page of a gated module needs "view"; its "/new" pages also need "create". Edit is checked on the
// detail page itself, which opens read-only without it.
const requiredOperations = (path: string): OperationId[] =>
  path === 'new' || path.endsWith('/new') ? ['view', 'create'] : ['view'];
